import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import Navbar from "./Navbar";
import API_CONFIG, { buildApiUrl } from '../config/api';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const [userGroups, setUserGroups] = useState([]);
  
  // Function to get belt color based on rank
  const getBeltColor = (rank) => {
    const colorMap = {
      'WHITE': 'rgba(255, 255, 255, 0.8)',      // White
      'YELLOW': 'rgba(255, 255, 0, 0.8)',       // Yellow
      'GREEN': 'rgba(0, 128, 0, 0.8)',          // Green
      'BLUE': 'rgba(0, 0, 255, 0.8)',           // Blue
      'RED': 'rgba(255, 0, 0, 0.8)',            // Red
      'PREBLACK': 'rgba(255, 0, 0, 0.8)',       // Red background for pre-black
      'BLACK': 'rgba(0, 0, 0, 0.8)',            // Black
      'JUNIOR BLACK': 'rgba(0, 0, 0, 0.6)'
    };
    return colorMap[rank] || 'rgba(128, 128, 128, 0.5)'; // Default gray for unknown ranks
  };

  // Function to get border color with special handling for PREBLACK and WHITE
  const getBeltBorderColor = (rank) => {
    if (rank === 'PREBLACK' || rank === 'JUNIOR PREBLACK' || rank === 'WHITE' || rank === 'JUNIOR WHITE') {
      return 'rgba(0, 0, 0, 1)'; // Black border for pre-black and white to simulate stripes
    }
    return getBeltColor(rank).replace('0.8', '1').replace('0.6', '1');
  };

  // Function to get border width with special handling for PREBLACK and WHITE
  const getBeltBorderWidth = (rank) => {
    if (rank === 'PREBLACK' || rank === 'JUNIOR PREBLACK') {
      return 6; // Twice as thick border for pre-black to make stripes more visible
    }
    if (rank === 'WHITE' || rank === 'JUNIOR WHITE') {
      return 2; // Thicker border for white to make stripes visible
    }
    return 1;
  };

  const [chartData1, setChartData1] = useState({
    labels: [],
    datasets: [{
      label: 'Number of Students',
      data: [],
      backgroundColor: [],
      borderColor: [],
      borderWidth: [],
    }]
  });
  const [chartData2, setChartData2] = useState({
    labels: [],
    datasets: [{
      label: 'Number of Students',
      data: [],
      backgroundColor: 'rgba(54, 162, 235, 0.5)',
      borderColor: 'rgba(54, 162, 235, 1)',
      borderWidth: 1,
    }]
  });

  const [chartData3, setChartData3] = useState({
    labels: [],
    datasets: [{
      label: 'Attendance Count',
      data: [],
      backgroundColor: 'rgba(75, 192, 192, 0.5)',
      borderColor: 'rgba(75, 192, 192, 1)',
      borderWidth: 1,
    }]
  });

  const [chartData4, setChartData4] = useState({
    labels: [],
    datasets: [{
      label: 'Unique Students',
      data: [],
      backgroundColor: 'rgba(255, 99, 132, 0.2)',
      borderColor: 'rgba(255, 99, 132, 1)',
      borderWidth: 2,
      tension: 0.4,
      fill: true
    }]
  });

  useEffect(() => {
    const token = localStorage.getItem("token"); // Retrieve token
        if (!token) {
          console.error("No authentication token found.");
          return;
        } else {
          const decoded = JSON.parse(atob(token.split(".")[1]));
          setUserGroups(decoded["cognito:groups"]);
        }

    const fetchStudentDataByRank = async () => {
      
      try {
        const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.REPORTING.STUDENTS_BY_RANK),{
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const data = await response.json();
        
        const labels = data.map(item => item.rank);
        const counts = data.map(item => item.count);
        
        // Generate colors for each bar based on belt rank
        const backgroundColors = data.map(item => getBeltColor(item.rank));
        const borderColors = data.map(item => getBeltBorderColor(item.rank));
        const borderWidths = data.map(item => getBeltBorderWidth(item.rank));
        
        setChartData1({
          labels,
          datasets: [{
            ...chartData1.datasets[0],
            data: counts,
            backgroundColor: backgroundColors,
            borderColor: borderColors,
            borderWidth: borderWidths
          }]
        });
      } catch (error) {
        console.error('Error fetching student data:', error);
      }
    };

    const fetchStudentDataByStartDate = async () => {
      
      try {
        const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.REPORTING.STUDENTS_BY_START_DATE),{
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const data = await response.json();
        
        const labels = data.map(item => item.start_date);
        const counts = data.map(item => item.count);
        
        setChartData2({
          labels,
          datasets: [{
            ...chartData2.datasets[0],
            data: counts
          }]
        });
      } catch (error) {
        console.error('Error fetching student data:', error);
      }
    };

    const fetchClassAttendanceData = async () => {
      try {
        const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.REPORTING.CLASS_ATTENDANCE), {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const data = await response.json();
        
        // Get unique dates and class names
        const dates = [...new Set(data.map(item => item.class_date))].sort();
        const classNames = [...new Set(data.map(item => item.class_name))].sort();
        
        // Create datasets for each class
        const datasets = classNames.map((className, index) => {
          const colors = [
            'rgba(75, 192, 192, 0.5)',
            'rgba(153, 102, 255, 0.5)',
            'rgba(255, 159, 64, 0.5)',
            'rgba(255, 99, 132, 0.5)'
          ];
          
          // For each class, create an array of attendance counts for each date
          const attendanceData = dates.map(date => {
            const attendance = data.find(item => 
              item.class_date === date && item.class_name === className
            );
            return attendance ? attendance.attendance_count : 0;
          });

          return {
            label: className,
            data: attendanceData,
            backgroundColor: colors[index % colors.length],
            borderColor: colors[index % colors.length].replace('0.5', '1'),
            borderWidth: 1,
          };
        });

        setChartData3({
          labels: dates,
          datasets
        });
      } catch (error) {
        console.error('Error fetching class attendance data:', error);
      }
    };

    const fetchUniqueStudentsByDay = async () => {
      try {
        const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.REPORTING.UNIQUE_STUDENTS_BY_DAY), {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const data = await response.json();
        
        const labels = data.map(item => item.class_date);
        const counts = data.map(item => item.unique_students);
        
        setChartData4({
          labels,
          datasets: [{
            ...chartData4.datasets[0],
            data: counts
          }]
        });
      } catch (error) {
        console.error('Error fetching unique students data:', error);
      }
    };

    fetchStudentDataByRank();
    fetchStudentDataByStartDate();
    fetchClassAttendanceData();
    fetchUniqueStudentsByDay();
  }, []);

  const chart1Options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Students by Belt Rank'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  };

  const chart2Options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Students by Start Date'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  };

  const chart3Options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Class Attendance by Date'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        stacked: true,
        ticks: {
          stepSize: 1
        }
      },
      x: {
        stacked: true
      }
    }
  };

  const chart4Options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Unique Students per Day'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  };

  return (
    <Container fluid>
      <Row className="mt-4">
        <Col>
           <Navbar groups={userGroups}/>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col>
          <Card>
            <Card.Body>
              <Bar data={chartData1} options={chart1Options} />
            </Card.Body>
          </Card>
        </Col>
        <Col>
          <Card>
            <Card.Body>
              <Line data={chartData2} options={chart2Options} />
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col>
          <Card>
            <Card.Body>
              <Bar data={chartData3} options={chart3Options} />
            </Card.Body>
          </Card>
        </Col>
        <Col>
          <Card>
            <Card.Body>
              <Line data={chartData4} options={chart4Options} />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Dashboard;
